import { useEffect, useState } from "react";
import { Icon } from "@omnabase/ui";
import { cardState, type CardState } from "../lib/pushCard";
import { currentSubscription, listBrowsers, permissionNow, pushSupported, rememberedRow, thisDevice } from "../lib/push";
import { isAppleTouch } from "../lib/device";
import { useMediaQuery } from "../lib/inbox";
import { NOTIFY_ROW, dismissNotifyRow, notifyRowDismissed, notifyRowShown } from "../lib/notifyRow";
import { useNav, useWs } from "../lib/nav";
import { useSession } from "../shell/store";

/** Home's notifications row (the home-and-customers round, part four): at the end of "Needs you", after the counted rows and before the foot,
 *  in a browser that could turn notifications on and has not. Not a need: a bell where the number sits, the muted ground, the title in the
 *  normal weight. A container holding the link (title and sub line, to the Device tab) and, where the chevron sits, a sibling Dismiss button
 *  (a button may not sit inside a link). The device is read lightly, as the one-time hint reads it (the key pair, the subscription, the
 *  permission; never a write), and not at all while away, on a phone or an iPhone, or once dismissed. */
export function HomeNotifyRow() {
  const nav = useNav(); const { ws, slug } = useWs();
  const { workspace } = useSession();
  const narrow = useMediaQuery("(max-width: 767px)");
  const [dismissed, setDismissed] = useState(() => notifyRowDismissed(slug));
  const phone = narrow || thisDevice().kind === "iphone";
  const skip = workspace.me.away || phone || dismissed;
  const state = useLightDeviceState(slug, skip);
  if (!notifyRowShown({ state, away: workspace.me.away, phone, dismissed })) return null;
  const href = ws(NOTIFY_ROW.href);
  const dismiss = () => { dismissNotifyRow(slug); setDismissed(true); };
  return (
    <div className="ob-needs__invite">
      <a className="ob-needs__invite-link" href={href} onClick={(e) => nav(href, e)}>
        <span className="ob-needs__count ob-needs__count--icon" aria-hidden="true"><Icon name="bell" size="sm" /></span>
        <span className="ob-needs__text"><span className="ob-needs__title ob-needs__title--quiet">{NOTIFY_ROW.title}</span><span className="ob-needs__sub">{NOTIFY_ROW.sub}</span></span>
      </a>
      <button type="button" className="ob-needs__dismiss" aria-label="Dismiss" title="Dismiss" onClick={dismiss}><Icon name="x" size="sm" /></button>
    </div>
  );
}

/** this browser's notification state for the workspace, read once ("loading" until then; "hidden" when skipped, so nothing shows) */
function useLightDeviceState(slug: string, skip: boolean): CardState {
  const [state, setState] = useState<CardState>("loading");
  useEffect(() => {
    if (skip) { setState("hidden"); return; }
    let live = true;
    void lightDeviceState(slug).then((s) => { if (live) setState(s); }, () => { if (live) setState("hidden"); });
    return () => { live = false; };
  }, [slug, skip]);
  return state;
}

/** the device panel's state without its writes: not supported and blocked answer before any request; else the key pair and the own row */
async function lightDeviceState(slug: string): Promise<CardState> {
  const device = thisDevice();
  if (!isAppleTouch(device.kind) && !pushSupported()) return "unsupported";
  const permission = await permissionNow();
  if (permission === "denied") return "blocked";
  const [listing, sub] = await Promise.all([listBrowsers(slug).catch(() => null), currentSubscription().catch(() => null)]);
  const own = rememberedRow(slug);
  return cardState({ publicKey: listing?.publicKey ?? null, supported: pushSupported(), permission, subscribed: !!sub, ownRow: listing?.subscriptions.find((s) => s.id === own) ?? null, device });
}
